import { createHighlighter } from 'shiki';
export * from 'shiki';
import { createJavaScriptRegexEngine } from 'shiki/engine/javascript';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var NewLineSymbol;
(function (NewLineSymbol) {
    NewLineSymbol[NewLineSymbol["CRLF"] = 1] = "CRLF";
    NewLineSymbol[NewLineSymbol["CR"] = 2] = "CR";
    NewLineSymbol[NewLineSymbol["LF"] = 3] = "LF";
    NewLineSymbol[NewLineSymbol["NEWLINE"] = 4] = "NEWLINE";
    NewLineSymbol[NewLineSymbol["NORMAL"] = 5] = "NORMAL";
    NewLineSymbol[NewLineSymbol["NULL"] = 6] = "NULL";
})(NewLineSymbol || (NewLineSymbol = {}));
var DiffModeEnum;
(function (DiffModeEnum) {
    // github like
    DiffModeEnum[DiffModeEnum["SplitGitHub"] = 1] = "SplitGitHub";
    // gitlab like
    DiffModeEnum[DiffModeEnum["SplitGitLab"] = 2] = "SplitGitLab";
    DiffModeEnum[DiffModeEnum["Split"] = 3] = "Split";
    DiffModeEnum[DiffModeEnum["Unified"] = 4] = "Unified";
})(DiffModeEnum || (DiffModeEnum = {}));

const processAST = (ast) => {
    let lineNumber = 1;
    const syntaxObj = {};
    const loopAST = (nodes, wrapper) => {
        nodes.forEach((node) => {
            if (node.type === "text") {
                if (node.value.indexOf("\n") === -1) {
                    const valueLength = node.value.length;
                    if (!syntaxObj[lineNumber]) {
                        node.startIndex = 0;
                        node.endIndex = valueLength - 1;
                        const item = {
                            value: node.value,
                            lineNumber,
                            valueLength,
                            nodeList: [{ node, wrapper }],
                        };
                        syntaxObj[lineNumber] = item;
                    }
                    else {
                        node.startIndex = syntaxObj[lineNumber].valueLength;
                        node.endIndex = node.startIndex + valueLength - 1;
                        syntaxObj[lineNumber].value += node.value;
                        syntaxObj[lineNumber].valueLength += valueLength;
                        syntaxObj[lineNumber].nodeList.push({ node, wrapper });
                    }
                    node.lineNumber = lineNumber;
                    return;
                }
                const lines = node.value.split("\n");
                node.children = node.children || [];
                for (let i = 0; i < lines.length; i++) {
                    const _value = i === lines.length - 1 ? lines[i] : lines[i] + "\n";
                    const _lineNumber = i === 0 ? lineNumber : ++lineNumber;
                    const _valueLength = _value.length;
                    const _node = {
                        type: "text",
                        value: _value,
                        startIndex: Infinity,
                        endIndex: Infinity,
                        lineNumber: _lineNumber,
                    };
                    if (!syntaxObj[_lineNumber]) {
                        _node.startIndex = 0;
                        _node.endIndex = _valueLength - 1;
                        const item = {
                            value: _value,
                            lineNumber: _lineNumber,
                            valueLength: _valueLength,
                            nodeList: [{ node: _node, wrapper }],
                        };
                        syntaxObj[_lineNumber] = item;
                    }
                    else {
                        _node.startIndex = syntaxObj[_lineNumber].valueLength;
                        _node.endIndex = _node.startIndex + _valueLength - 1;
                        syntaxObj[_lineNumber].value += _value;
                        syntaxObj[_lineNumber].valueLength += _valueLength;
                        syntaxObj[_lineNumber].nodeList.push({ node: _node, wrapper });
                    }
                    node.children.push(_node);
                }
                node.lineNumber = lineNumber;
                return;
            }
            if (node.children) {
                loopAST(node.children, node);
                node.lineNumber = lineNumber;
            }
        });
    };
    loopAST(ast.children);
    return { syntaxFileObject: syntaxObj, syntaxFileLineNumber: lineNumber };
};

const jsEngine = createJavaScriptRegexEngine({ forgiving: true });
let provider = null;
let defaultInternal = null;
let customInternal = null;
let customInternalLangs = null;
const getDefaultHighlighter = async () => {
    let i = defaultInternal;
    if (!i) {
        i = await createHighlighter({
            themes: ["github-light", "github-dark"],
            langs: [
                "cpp",
                "java",
                "javascript",
                "css",
                "c#",
                "c",
                "c++",
                "vue",
                "vue-html",
                "astro",
                "bash",
                "make",
                "markdown",
                "makefile",
                "bat",
                "cmake",
                "cmd",
                "csv",
                "docker",
                "dockerfile",
                "go",
                "python",
                "html",
                "jsx",
                "tsx",
                "typescript",
                "sql",
                "xml",
                "sass",
                "ssh-config",
                "kotlin",
                "json",
                "swift",
                "txt",
                "diff",
            ],
            engine: jsEngine,
        });
        defaultInternal = i;
    }
    provider = i;
    return i;
};
const getHighlighter = async (langs) => {
    if (Array.isArray(langs) && langs.length > 0) {
        let i = customInternal;
        if (!i) {
            i = await createHighlighter({
                themes: ["github-light", "github-dark"],
                langs,
                engine: jsEngine,
            });
            customInternal = i;
            customInternalLangs = langs;
        }
        else {
            if (process.env.NODE_ENV === "development") {
                if (customInternalLangs) {
                    if (customInternalLangs.length < langs.length) {
                        console.warn("[@git-diff-view/shiki] Custom languages were already set, but the new list differs from the previous one. Please check your code.");
                    }
                    else if (!customInternalLangs.sort().join(",").startsWith(langs.sort().join(","))) {
                        console.warn("[@git-diff-view/shiki] Custom languages were already set, and the new list is not a subset of the previous one. Please check your code.");
                    }
                }
            }
        }
        provider = i;
        return i;
    }
    else {
        return await getDefaultHighlighter();
    }
};
const instance = { name: "shiki" };
let _maxLineToIgnoreSyntax = 2000;
const _ignoreSyntaxHighlightList = [];
Object.defineProperty(instance, "maxLineToIgnoreSyntax", {
    get: () => _maxLineToIgnoreSyntax,
});
Object.defineProperty(instance, "setMaxLineToIgnoreSyntax", {
    value: (v) => {
        _maxLineToIgnoreSyntax = v;
    },
});
Object.defineProperty(instance, "ignoreSyntaxHighlightList", {
    get: () => _ignoreSyntaxHighlightList,
});
Object.defineProperty(instance, "setIgnoreSyntaxHighlightList", {
    value: (v) => {
        _ignoreSyntaxHighlightList.length = 0;
        _ignoreSyntaxHighlightList.push(...v);
    },
});
Object.defineProperty(instance, "getAST", {
    value: (raw, fileName, lang) => {
        if (fileName &&
            highlighter.ignoreSyntaxHighlightList.some((item) => item instanceof RegExp ? item.test(fileName) : fileName === item)) {
            if (process.env.NODE_ENV === "development") {
                console.warn(`[@git-diff-view/shiki] Skipping syntax highlighting for "${fileName}" because it is listed in ignoreSyntaxHighlightList.`);
            }
            return;
        }
        try {
            return (provider || customInternal || defaultInternal)?.codeToHast(raw, {
                lang: lang ?? "",
                themes: {
                    dark: "github-dark",
                    light: "github-light",
                },
                cssVariablePrefix: "--diff-view-",
                defaultColor: false,
                mergeWhitespaces: false,
            });
        }
        catch (e) {
            if (process.env.NODE_ENV === "development") {
                console.error(e);
            }
            else {
                console.log(e.message);
            }
            return;
        }
    },
});
Object.defineProperty(instance, "processAST", {
    value: (ast) => {
        return processAST(ast);
    },
});
Object.defineProperty(instance, "hasRegisteredCurrentLang", {
    value: (lang) => {
        return (provider || customInternal || defaultInternal)?.getLanguage(lang) !== undefined;
    },
});
Object.defineProperty(instance, "getHighlighterEngine", {
    value: () => {
        return provider || customInternal || defaultInternal;
    },
});
Object.defineProperty(instance, "type", { value: "class" });
const highlighter = instance;
// TODO: change to use function
/**
 * @deprecated
 * try to use `getDiffViewHighlighter` instead
 *
 * @example
 * ```ts
 * import { highlighterReady } from '@git-diff-view/shiki'
 *
 * highlighterReady.then((highlighter) => {
 *  // do something with highlighter
 * })
 * ```
 */
const highlighterReady = new Promise((r) => {
    getDefaultHighlighter().then(() => r(highlighter));
}).then((r) => {
    const proxy = new Proxy(r, {
        get(target, prop, receiver) {
            if (typeof prop === "string" && prop !== "then") {
                console.warn("[@git-diff-view/shiki] `highlighterReady` is deprecated; use `getDiffViewHighlighter` instead.");
            }
            return Reflect.get(target, prop, receiver);
        },
    });
    return proxy;
});
/**
 * get the shiki diffView highlighter
 * @returns Promise<DiffHighlighter>
 *
 * @example
 * ```ts
 * import { getDiffViewHighlighter } from '@git-diff-view/shiki'
 *
 * getDiffViewHighlighter().then((highlighter) => {
 *  // do something with highlighter
 * }
 * ```
 */
const getDiffViewHighlighter = async (langs) => {
    if (Array.isArray(langs) && langs.length > 0) {
        await getHighlighter(langs);
        return highlighter;
    }
    else {
        await getDefaultHighlighter();
        return highlighter;
    }
};
const versions = "0.1.8";

export { getDiffViewHighlighter, highlighterReady, processAST, versions };
//# sourceMappingURL=index.mjs.map
